import objectPath from 'object-path';
import { load } from 'recaptcha-v3';
import {
  ApolloClient,
  Observable as ApolloObservable,
} from '@apollo/client/core';
import Alert from 'react-s-alert';
import { observable } from 'mobx';
import { InMemoryCache } from '@apollo/client/cache';
import { ApolloLink, HttpLink, from, split, fromPromise } from '@apollo/client';
import { getMainDefinition } from '@apollo/client/utilities';
import { onError } from '@apollo/client/link/error';
import { RestLink } from 'apollo-link-rest';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient } from 'graphql-ws';
import { setContext } from '@apollo/client/link/context';

import { fromLocalStorage } from 'core/localStorage';
import { analytics } from 'core/analytics';
import { refresher, getJwt, getRefreshJwt } from 'core/jwt';
import { ErrorCode } from 'core/errors';

import locationEndpoints from 'libs/location/api';
import uuid from 'uuid/v4';

const possibleTypes = {
  ContentInterface: [
    'ContentInterface',
    'Issue',
    'Initiative',
    'Profile',
    'Story',
    'Thread',
    'File',
    'Project',
    'Comment',
    'Contribution',
  ],
  NotificationTarget: [
    'Contribution',
    'Project',
    'Story',
    'Profile',
    'Thread',
    'Initiative',
    'Comment',
    'Issue',
    'GroupInvite',
    'HTMLNotification',
  ],
};

// observable for the status of the client
export const subscriptionStatusObservable = observable({
  status: 'initialized',
});

const apolloLinks = [];
let wsLink = null;
let wsHmacLink = null;
const subscriptionQueryLinks = [];

if (SUBSCRIPTION_SERVER_ENDPOINT) {
  wsLink = new GraphQLWsLink(
    createClient({
      url: SUBSCRIPTION_SERVER_ENDPOINT,
      connectionParams: () => {
        const jwt = getJwt();
        const refreshJwt = getRefreshJwt();
        if (jwt === 'null' || refreshJwt === 'null') {
          return {};
        }
        return jwt && refreshJwt
          ? { Authorization: `Bearer ${jwt}`, RefreshToken: refreshJwt }
          : {};
      },
      connectionCallback: async error => {
        if (error) {
          if (error?.message?.includes('token')) {
            await refresher.refreshToken();
          }
        }
      },
      lazy: true,
      shouldRetry: () => true,
      on: {
        closed: () => {
          // eslint-disable-next-line no-console
          console.log('Connection closed from subscription server');
          subscriptionStatusObservable.status = 'disconnected';
        },
        opened: () => {
          // eslint-disable-next-line no-console
          console.log('Subscription server connection reconnected');
          subscriptionStatusObservable.status = 'connected';
        },
        connected: () => {
          // eslint-disable-next-line no-console
          console.log('Connect to subscription server now');
          subscriptionStatusObservable.status = 'connected';
        },
      },
    }),
  );

  subscriptionQueryLinks.push(
    new HttpLink({
      uri: SUBSCRIPTION_SERVER_ENDPOINT.replace('ws', 'http'),
    }),
  );
}

export const encodeUsername = username =>
  btoa(unescape(encodeURIComponent(username)));

export const authFetch = (url, args = {}) => {
  let username = '';
  const { HMAC } = args;
  try {
    let profileObject = args.signedInProfile;
    if (!profileObject) {
      profileObject = fromLocalStorage('signedInProfile');
    }

    const decodedUsername = objectPath.get(profileObject, 'username');
    if (decodedUsername && typeof decodedUsername === 'string') {
      username = encodeUsername(profileObject.username.slice(1));
    }
  } catch (error) {
    analytics.logError(error);
  }
  if (username) {
    const oldHeaders = args.headers;
    args.headers = {
      ...oldHeaders,
      'CLIENT-USERNAME': username,
    };
  }
  const jwt = getJwt();
  if (jwt && jwt !== 'null') {
    const oldHeaders = args.headers;
    args.headers = {
      ...oldHeaders,
      Authorization: `Bearer ${jwt}`,
    };
  }
  if (!username && !jwt && HMAC) {
    const oldHeaders = args.headers;
    args.headers = {
      ...oldHeaders,
      'X-Wikifactory-Hmac': HMAC,
    };
  }
  return fetch(url, args);
};

const jwtMiddleware = onError(
  ({ graphQLErrors, networkError, operation, forward }) => {
    if (graphQLErrors) {
      // eslint-disable-next-line no-restricted-syntax
      for (const err of graphQLErrors) {
        if (
          err.extensions &&
          err.extensions?.code === ErrorCode.INVALID_TOKEN
        ) {
          return fromPromise(refresher.refreshToken()).flatMap(newToken => {
            if (!newToken) return undefined;
            // No need to inject the new token into the operation because the next step
            // is authMiddleware, which will see the refreshed token
            return forward(operation);
          });
        }
      }
    }

    if (networkError) {
      console.error(`[Network error]: ${networkError}`);
      return forward(operation);
    }
    return null;
  },
);

// so when we are impersonating as other user, we can't perform mutations
const mutationForbiddenLink = new ApolloLink((operation, forward) => {
  let isMutation = false;

  operation.query.definitions.forEach(definition => {
    if (
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'mutation'
    ) {
      isMutation = true;
    }
  });

  // disable the mutation if we are impersonating
  if (sessionStorage.getItem('isImpersonating') === 'true' && isMutation) {
    Alert.error('You cannot perform this action while impersonating.');

    return new ApolloObservable(() => {}); // Return an empty observable to prevent the mutation
  }

  // allow the query
  return forward(operation);
});

const authMiddleware = new ApolloLink((operation, forward) => {
  let username = '';
  try {
    const profileObject = fromLocalStorage('signedInProfile');
    const decodedUsername = objectPath.get(profileObject, 'username');
    if (decodedUsername && typeof decodedUsername === 'string') {
      username = encodeUsername(profileObject.username.slice(1));
    }
  } catch (error) {
    analytics.logError(error);
  }
  if (username) {
    const oldHeaders = operation.getContext().headers;
    const context = {
      headers: {
        ...oldHeaders,
        'CLIENT-USERNAME': username,
      },
    };
    const jwt = getJwt();
    if (jwt && jwt !== 'null') {
      context.headers.Authorization = `Bearer ${jwt}`;
    }
    operation.setContext(context);
  }

  return forward(operation);
});

apolloLinks.push(mutationForbiddenLink);
apolloLinks.push(jwtMiddleware);
apolloLinks.push(authMiddleware);
subscriptionQueryLinks.unshift(authMiddleware);
subscriptionQueryLinks.unshift(jwtMiddleware);

class Recaptcha {
  async loadRecaptcha() {
    // NEED API TOKEN PAIR FOR ALL MICROSITES
    // Consider Language codes for internationalisation - if we use recaptcha 2 for sign-up
    if (!this.recaptcha) {
      this.recaptcha = await load(RECAPTCHA_SITE_KEY, {
        autoHideBadge: true,
        useRecaptchaNet: true,
      });
    }
    return this.recaptcha;
  }
}

if (RECAPTCHA_SITE_KEY) {
  const recaptchaStore = new Recaptcha();

  const recaptchaLink = setContext(async (operation, { headers }) => {
    const gqlType = getMainDefinition(operation.query).operation;
    if (gqlType === 'mutation') {
      const recaptcha = await recaptchaStore.loadRecaptcha();
      // Restrict to only certain mutations?
      // Use mutation name for 'action' - or group actions into broader types?
      const recaptchaToken = await recaptcha.execute('mutate');
      return {
        headers: {
          ...headers,
          'RECAPTCHA-TOKEN': recaptchaToken,
        },
      };
    }
    return { headers };
  });

  apolloLinks.push(recaptchaLink);
}

const namedLink = new ApolloLink((operation, forward) => {
  operation.setContext(() => ({
    uri: `${API_URL_BASE}/graphql?${operation.operationName}`,
  }));
  return forward ? forward(operation) : null;
});

apolloLinks.push(namedLink);

const inflights = {};

const abortableLink = new ApolloLink((operation, forward) => {
  // make using this link configurable by query?
  const requestId = uuid();

  const controller = new AbortController();
  const origContext = operation.getContext();
  if (origContext.abortOnRerequest) {
    operation.setContext({
      ...origContext,
      controller,
      fetchOptions: {
        signal: controller.signal,
      },
    });
    Object.keys(inflights).forEach(key => {
      if (key.startsWith(operation.operationName)) {
        inflights[key][0].abort();
        delete inflights[key];
      }
    });
    inflights[`${operation.operationName}-${requestId}`] = [
      controller,
      operation,
    ];
  }
  return forward(operation);
});

apolloLinks.push(abortableLink);

if (locationEndpoints) {
  const restLink = new RestLink({ endpoints: locationEndpoints });
  apolloLinks.push(restLink);
}

const httpLink = new HttpLink({
  uri: `${API_URL_BASE}/graphql`,
  credentials: 'include',
});

apolloLinks.push(httpLink);

const cache = new InMemoryCache({ possibleTypes });

const defaultOptions = {
  watchQuery: {
    fetchPolicy: 'no-cache',
    errorPolicy: 'none',
  },
  query: {
    fetchPolicy: 'no-cache',
    errorPolicy: 'none',
  },
  mutate: {
    errorPolicy: 'none', // raise sysErrors on graphql errors
    // fetchPolicy: 'no-cache'
  },
};

const linkWithWs = split(
  // split based on operation type, send subscriptions through websocket link
  ({ query }) => {
    const definition = getMainDefinition(query);
    return (
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'subscription'
    );
  },
  wsLink,
  from(apolloLinks),
);

export const client = new ApolloClient({
  link: from(apolloLinks),
  cache,
  defaultOptions,
  dataIdFromObject: result => {
    if (result.id !== undefined) {
      return `${result.id}`;
    }
    return null;
  },
  connectToDevTools: CI || ENV_NAME === 'dev',
});

export const initWebsocket = () => {
  // if (wsLink.subscriptionClient.status === 1) return;
  // // just in case
  // wsLink.subscriptionClient.close();

  client.setLink(linkWithWs);
};

export const initHMACWebsocket = HMAC => {
  // just in case of some race condition, we don't need to setup an HMAC subscription if we have a jwt.
  if (localStorage.getItem('jwt')) return;
  wsHmacLink = new GraphQLWsLink(
    createClient({
      url: SUBSCRIPTION_SERVER_ENDPOINT,
      connectionParams: () => {
        return { HMAC };
      },
      lazy: true,
      shouldRetry: () => true,
    }),
  );

  const linkWithHmacWs = split(
    // split based on operation type, send subscriptions through websocket link
    ({ query }) => {
      const definition = getMainDefinition(query);
      return (
        definition.kind === 'OperationDefinition' &&
        definition.operation === 'subscription'
      );
    },
    wsHmacLink,
    from(apolloLinks),
  );
  client.setLink(linkWithHmacWs);
  // wsHmacLink.subscriptionClient.connect();
};

export const disconnectWebsocket = () => {
  wsLink.client.dispose();
};
